import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { loginRequest } from '../redux/slices/authSlice';
import snapmenuLogo from '../assets/snapmenu.png';
import { TurnstileVerification } from './common/TurnstileVerification';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated, pendingUser, sendOtp, user } = useAppSelector(
    state => state.auth
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);

  // Prevent back navigation from login page
  useEffect(() => {
    // Push initial state to prevent back navigation
    window.history.pushState(null, '', window.location.href);
    
    const handlePopState = () => {
      // When user tries to go back, push state again to block it
      window.history.pushState(null, '', window.location.href);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Reset verification state when login fails
  useEffect(() => {
    if (error) {
      setTurnstileToken(null);
    }
  }, [error]);

  // Navigate to OTP page when sendOtp succeeds (after login success and OTP sent)
  useEffect(() => {
    if (sendOtp.success && pendingUser) {
      navigate('/otp-verification', { replace: true });
    }
  }, [sendOtp.success, pendingUser, navigate]);

  // Redirect to API overview when authenticated (after OTP verification)
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/api-reference/introduction', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(loginRequest({ email, password }));
  };

  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Welcome!</h2>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <h3 className="text-lg font-medium text-green-800">
                Login Successful
              </h3>
              <p className="text-green-600">
                Hello, {user?.firstName} {user?.lastName}
              </p>
              <p className="text-sm text-gray-600 mt-2">Email: {user?.email}</p>
            </div>
            <pre className="mt-4 text-xs text-left bg-gray-100 p-4 rounded overflow-auto">
              {JSON.stringify(user, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Logo/Header Section */}
          <div className="px-8 py-8 text-center bg-white">
            <div className="flex justify-center mb-6">
              <img 
                src={snapmenuLogo} 
                alt="SnapMenuAI Logo" 
                className="h-16 object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign In to Your Account</h2>
          </div>

          {/* Form Section */}
          <div className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <IoMdEyeOff size={20} /> : <IoMdEye size={20} />}
                  </button>
                </div>
              </div>

              {/* Turnstile Human Verification */}
              <TurnstileVerification
                onVerified={(token) => setTurnstileToken(token)}
                onError={(err) => setTurnstileError(err)}
                onReset={() => setTurnstileToken(null)}
                className="my-4"
              />

              {/* Display Turnstile error if any */}
              {turnstileError && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-red-700 text-sm font-medium">{turnstileError}</p>
                </div>
              )}

              {/* Display login error if any */}
              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !turnstileToken}
                className="w-full bg-red-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            {/* Signup and Forgot Password Links */}
            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-gray-600">
                New to SnapMenuAI?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/signup')}
                  className="text-red-500 hover:text-red-600 font-medium transition-colors"
                >
                  Signup
                </button>
              </p>
              <p className="text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-gray-600 hover:underline transition-colors"
                >
                  Forgot Password?
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
